import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import { FC, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import SignaturePad from 'react-signature-pad-wrapper'

import { TABLET_WIDTH_BREAKPOINT } from '../../config/breakPoints'
import { WindowSizeContext } from '../../providers'
import {
  useCreateDigitalAuthorizationMutation,
  useGetServicesByApiKeyQuery,
  useUpdateServiceViaApiKeyMutation
} from '../../redux/api/inspectionApi'
import {
  AuthorizationInfoViaEnum,
  RepairOrderExpanded,
  ServiceEntry
} from '../../sdk'
import { SlideUpDialogTransition } from '../SharedUI/SlideUpDialogTransition'
import { CustomerAuthorizationTable } from './CustomerAuthorizationTable'

interface Props {
  dialogOpen: boolean
  handleClose: () => void
  previousTotal?: number
  repairOrder: RepairOrderExpanded | undefined
  shopName: string
  setLoading: React.Dispatch<React.SetStateAction<boolean>>
}

export const CustomerAuthorizationDialog: FC<Props> = ({
  dialogOpen,
  handleClose,
  previousTotal,
  repairOrder,
  shopName,
  setLoading
}) => {
  const { width } = useContext(WindowSizeContext)
  const overTabletBreakpoint = width >= TABLET_WIDTH_BREAKPOINT

  const { apiKey } = useParams()
  const { data: services } = useGetServicesByApiKeyQuery({
    xAPIKey: apiKey!
  })

  const [authorizerName, setAuthorizerName] = useState<string>('')
  const [
    createDigitalAuthorization,
    { isLoading: loadingDigitalAuthorization }
  ] = useCreateDigitalAuthorizationMutation()
  const [updateServiceViaApiKey, { isLoading: loadingUpdateService }] =
    useUpdateServiceViaApiKeyMutation()

  useEffect(() => {
    setLoading(loadingUpdateService)
  }, [loadingUpdateService, setLoading])

  const [warning, setWarning] = useState<string>('')

  const newTotal = repairOrder?.authorizedPricing!.total!
  const newAuthDiff = newTotal - (previousTotal || 0)

  const signaturePadRef = useRef<SignaturePad>(null)

  const handleCancel = useCallback(() => {
    setAuthorizerName('')
    setWarning('')
    handleClose()
  }, [handleClose])

  const handleCreateDigitalAuthorization = useCallback(async () => {
    // validate on submit else alert will show up on dialog open
    if (authorizerName.length < 1) {
      setWarning(
        'Please type your full name before authorizing (min 1 character)'
      )
      return
    }

    // occasionally bugs out if validate before submit button is clicked
    if (signaturePadRef.current?.isEmpty()) {
      setWarning('Please sign before authorizing')
      return
    }

    await createDigitalAuthorization({
      xAPIKey: apiKey!,
      authorizationInfo: {
        shop: repairOrder!.entry!.info!.shop!,
        repairOrder: repairOrder!.entry!.id!,
        dateTime: new Date(),
        amount: newTotal,
        via: AuthorizationInfoViaEnum.CustomerPortal,
        authorizerName,
        signature: signaturePadRef.current?.toDataURL()
      }
    })

    handleCancel()
  }, [
    apiKey,
    authorizerName,
    createDigitalAuthorization,
    handleCancel,
    newTotal,
    repairOrder
  ])

  const handleDeferService = useCallback(
    async (service: ServiceEntry, isDeferral: boolean) => {
      if (!apiKey) {
        return
      }
      await updateServiceViaApiKey({
        xAPIKey: apiKey,
        customerServiceStatusRequest: {
          serviceId: service.id!,
          isDeferral
        }
      })
    },
    [apiKey, updateServiceViaApiKey]
  )

  return (
    <Dialog
      open={dialogOpen}
      fullWidth={true}
      maxWidth="sm"
      fullScreen={overTabletBreakpoint ? undefined : true}
      TransitionComponent={
        overTabletBreakpoint ? undefined : SlideUpDialogTransition
      }>
      <DialogTitle>
        Confirm Service Authorization
        {warning !== '' && <Alert color="warning">{warning}</Alert>}
        {newAuthDiff < 0.01 && (
          <Alert color="info">No additional authorization needed</Alert>
        )}
      </DialogTitle>
      <DialogContent>
        <CustomerAuthorizationTable
          serviceItems={services || []}
          previousTotal={previousTotal}
          newTotal={newTotal}
          handleDeferService={(entry) => handleDeferService(entry, true)}
          handleRecommendService={(entry) => handleDeferService(entry, false)}
        />

        {newAuthDiff >= 0.01 && (
          <Stack
            direction="column"
            spacing={2}
            sx={{
              display: 'flex',
              alignItems: 'center',
              pt: 1,
              pb: 2
            }}>
            <Typography>
              Please type your full name and sign below to confirm:
            </Typography>
            <TextField
              name="authorizerName"
              label={'Full Name'}
              type="text"
              value={authorizerName}
              sx={{ width: '300px' }}
              onChange={(event) => {
                setAuthorizerName(event?.target.value)
              }}
            />
            <Box>
              <Typography>Sign here:</Typography>
              <div
                style={{
                  border: '1px solid black',
                  width: '300px',
                  maxHeight: '400px'
                }}>
                <SignaturePad ref={signaturePadRef} />
              </div>
            </Box>
          </Stack>
        )}

        {newAuthDiff >= 0.01 && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="caption">
                By clicking Confirm, you are certifying that you have the legal
                right to authorize repairs for the above-listed vehicle, either
                through legal ownership, lease rights, or written authorization
                from the vehicle&apos;s owner. Further, you authorize {shopName}{' '}
                to take the steps necessary to perform the repairs listed in
                this contract, including any necessary on-road vehicle testing.
                By typing your full name below and signing using the electronic
                signature pad provided, you acknowledge that you are providing
                your electronic signature with the intent to sign electronically
                and consent to do business electronically. You understand that
                your electronic signature will be associated with the record of
                this authorization contract and will be retained by {shopName}{' '}
                for record-keeping purposes.
              </Typography>
            </Grid>
            <Grid
              item
              xs={12}
              style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                disabled={loadingDigitalAuthorization}
                onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                variant="contained"
                disabled={loadingDigitalAuthorization}
                onClick={handleCreateDigitalAuthorization}>
                {loadingDigitalAuthorization ? 'Authorizing...' : 'Confirm'}
              </Button>
            </Grid>
          </Grid>
        )}
      </DialogContent>
      {newAuthDiff < 0.01 && (
        <DialogActions>
          <Button onClick={handleCancel}>Close</Button>
        </DialogActions>
      )}
    </Dialog>
  )
}
